import { inject, injectable } from "inversify";
import * as THREE from "three";
import { type IService, Logger, type PointerPosition } from "@designboticteam/core";
import { getParcelsInBbox, MapLibreInstanceService } from "@designboticteam/maplibre";
import { isPointInPolygonXZ, RaycastService } from "@designboticteam/workspace-3d";

type PickedParcel = {
	id: string;
	numer: string;
	areaM2: number;
	ringWgs84: Array<[number, number]>;
	scenePoints: THREE.Vector3[];
};

/** Finds the cadastral parcel under a pointer position: ground raycast → FlatGeobuf query → point-in-polygon. Stateless. */
@injectable()
class ParcelPickerService implements IService {
	public readonly serviceName = "ParcelPickerService";
	private readonly logger = new Logger(`📐 ${this.serviceName}`, { color: "#ffffff", backgroundColor: "#0891b2" });

	constructor(
		@inject(RaycastService) private readonly raycastService: RaycastService,
		@inject(MapLibreInstanceService) private readonly mapLibreInstanceService: MapLibreInstanceService,
	) {
		this.logger.log("constructed");
	}

	public initialize(): void {
		this.logger.log("initialized");
	}

	public dispose(): void {
		this.logger.log("disposed");
	}

	public async pickParcelAt(pointerPosition: PointerPosition): Promise<PickedParcel | null> {
		const groundPoint = this.raycastService.pickGroundPointAt(pointerPosition);

		if (groundPoint === null) {
			return null;
		}

		const [lng, lat] = this.mapLibreInstanceService.sceneToWgs84({ x: groundPoint.x, z: groundPoint.z });
		const features = await getParcelsInBbox({ minX: lng, minY: lat, maxX: lng, maxY: lat });

		for (const feature of features) {
			const parcel = this.featureToParcel(feature);

			if (parcel !== null && isPointInPolygonXZ(groundPoint, parcel.scenePoints)) {
				return parcel;
			}
		}

		return null;
	}

	public ringToScenePoints(ring: Array<[number, number]>): THREE.Vector3[] {
		return ring.map(([lng, lat]) => new THREE.Vector3(...this.mapLibreInstanceService.wgs84ToScene([lng, lat])));
	}

	private featureToParcel(feature: GeoJSON.Feature): PickedParcel | null {
		const properties = feature.properties ?? {};
		const id = typeof properties.parcel__id_dzialki === "string" ? properties.parcel__id_dzialki : "";

		if (id === "") {
			return null;
		}

		const ringWgs84 = this.openRing(this.extractOuterRing(feature.geometry));

		if (ringWgs84.length < 3) {
			return null;
		}

		const numer = typeof properties.parcel__numer_dzialki === "string" && properties.parcel__numer_dzialki !== "" ? properties.parcel__numer_dzialki : id;
		const areaM2 = Number(properties.parcel__pole_geometryczne);

		return {
			id,
			numer,
			areaM2: Number.isFinite(areaM2) ? areaM2 : 0,
			ringWgs84,
			scenePoints: this.ringToScenePoints(ringWgs84),
		};
	}

	private extractOuterRing(geometry: GeoJSON.Geometry): Array<[number, number]> {
		if (geometry.type === "Polygon") {
			return geometry.coordinates[0] as Array<[number, number]>;
		}

		if (geometry.type === "MultiPolygon") {
			return (geometry.coordinates[0]?.[0] ?? []) as Array<[number, number]>;
		}

		return [];
	}

	private openRing(ring: Array<[number, number]>): Array<[number, number]> {
		// GeoJSON rings are closed (first == last) — drop the duplicate so the open ring matches scenePoints.
		if (ring.length > 1) {
			const first = ring[0];
			const last = ring[ring.length - 1];

			if (first[0] === last[0] && first[1] === last[1]) {
				return ring.slice(0, -1);
			}
		}

		return ring;
	}
}

export { ParcelPickerService, type PickedParcel };
